import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_LOGO } from '../../config/defaults';
import { useDisplaySettings } from '../../hooks/useDisplaySettings';
import { Category, loadCategories } from '../../utils/categories';
import sitePaths from '../../utils/sitePaths';
import { Gallery } from '../../types/Gallery';
import './Navbar.css';
import '../ui-master-styles.css';

const categoriesWithGalleries = (categories: Category[], galleries: Gallery[]) => {
  const published = galleries.filter(gallery => gallery.published !== false);
  return [...categories]
    .filter(category => {
      const slug = category.slug || '';
      if (!slug) return false;
      return published.some(gallery => sitePaths.galleryInFilter(gallery, slug));
    })
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999) || a.name.localeCompare(b.name));
};

const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminPage = location.pathname.startsWith('/admin');
  const isHomePage = location.pathname === '/';
  const [logo, setLogo] = useState(DEFAULT_LOGO);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const [portfolioCategories, setPortfolioCategories] = useState<Category[]>([]);
  const [galleriesMenuOpen, setGalleriesMenuOpen] = useState(false);
  const [galleriesMenuDismissed, setGalleriesMenuDismissed] = useState(false);
  const displaySettings = useDisplaySettings();
  const galleryCategoryMatch = location.pathname.match(/^\/galleries\/([^/]+)$/);
  const isGalleriesRoute = location.pathname === '/galleries' || Boolean(galleryCategoryMatch);
  const activeGalleryCategory = galleryCategoryMatch ? (sitePaths.normalizeFilterSlug(galleryCategoryMatch[1]) || '') : '';

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1280);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadPortfolioMenu = async () => {
      try {
        const { loadLiveCategories } = await import('../../utils/CategoryAPI');
        const { loadLiveGalleries } = await import('../../utils/GalleryAPI');
        const [serverCategories, galleries] = await Promise.all([
          loadLiveCategories(),
          loadLiveGalleries('cards'),
        ]);
        const categories = serverCategories.length > 0 ? serverCategories : loadCategories();
        if (!cancelled) {
          setPortfolioCategories(categoriesWithGalleries(categories, galleries));
        }
      } catch (error) {
        console.error('Error loading gallery menu:', error);
        if (!cancelled) {
          setPortfolioCategories(loadCategories());
        }
      }
    };

    loadPortfolioMenu();
    window.addEventListener('categories_updated', loadPortfolioMenu);
    window.addEventListener('galleries_updated', loadPortfolioMenu);

    return () => {
      cancelled = true;
      window.removeEventListener('categories_updated', loadPortfolioMenu);
      window.removeEventListener('galleries_updated', loadPortfolioMenu);
    };
  }, []);

  const galleriesMenuLock = useRef(false);

  const openHomepageInstagram = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (isMobile) setIsMenuOpen(false);
    setGalleriesMenuOpen(false);

    if (!isHomePage) {
      navigate('/#instagram');
      return;
    }

    const instagram = document.getElementById('instagram');
    if (instagram) {
      const nav = document.querySelector('.navbar');
      const navHeight = nav ? Math.ceil(nav.getBoundingClientRect().height) : 70;
      window.scrollTo({
        top: instagram.getBoundingClientRect().top + window.scrollY - navHeight - 12,
        behavior: 'smooth',
      });
    }
    setActiveSection('instagram');
    if (location.hash !== '#instagram') {
      window.history.pushState(null, '', '/#instagram');
    }
  };

  const dismissGalleriesMenu = (event?: React.MouseEvent<HTMLElement>) => {
    galleriesMenuLock.current = true;
    setGalleriesMenuOpen(false);
    setGalleriesMenuDismissed(true);
    if (isMobile) setIsMenuOpen(false);
    event?.currentTarget.blur();
  };

  useEffect(() => {
    setGalleriesMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (isMobile && !isMenuOpen) {
      setGalleriesMenuOpen(false);
    }
  }, [isMobile, isMenuOpen]);

  useEffect(() => {
  const loadLogo = () => {
    const logoData = localStorage.getItem('website_logo');
    
    if (logoData) {
      try {
        const logoParsed = JSON.parse(logoData);
        
        // Ensure we have a valid imageUrl
        if (logoParsed.imageUrl && logoParsed.imageUrl.trim() !== '') {
          // Simplified URL - no cache busting on mobile as it causes CORS issues
          const imageUrl = logoParsed.imageUrl;
          
          setLogo({ 
            imageUrl: imageUrl,
            altText: logoParsed.altText || DEFAULT_LOGO.altText,
            publicId: logoParsed.publicId || DEFAULT_LOGO.publicId || '',
            width: logoParsed.width || DEFAULT_LOGO.width || 0,
            height: logoParsed.height || DEFAULT_LOGO.height || 0
          });
        } else {
          setLogo({
            imageUrl: DEFAULT_LOGO.imageUrl || '/carmen-logo.png',
            altText: logoParsed.altText || DEFAULT_LOGO.altText,
            publicId: logoParsed.publicId || DEFAULT_LOGO.publicId || '',
            width: logoParsed.width || DEFAULT_LOGO.width || 0,
            height: logoParsed.height || DEFAULT_LOGO.height || 0
          });
        }
      } catch (error) {
        console.error('Error parsing logo data:', error);
        setLogo(DEFAULT_LOGO);
      }
    } else {
      // Set a default logo if none exists
      setLogo(DEFAULT_LOGO);
    }
  };

    // Load logo immediately
    loadLogo();
    
    // Also load logo after a short delay to handle any localStorage timing issues
    const delayedLoad = setTimeout(loadLogo, 100);
    
    // Listen for logo updates and screen size changes
    const handleLogoUpdate = () => {
      loadLogo();
    };
    
    let lastWidth = window.innerWidth;
    let resizeTimer: NodeJS.Timeout;
    const handleResize = () => {
      // Debounce and only reload logo if width actually changed (not just scroll)
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const currentWidth = window.innerWidth;
        // Only reload logo if width meaningfully changed (not just scroll)
        if (Math.abs(currentWidth - lastWidth) >= 10) {
          lastWidth = currentWidth;
          loadLogo();
        }
      }, 300); // Longer debounce to ignore scroll-related resize events
    };
    
    window.addEventListener('logo_updated', handleLogoUpdate);
    window.addEventListener('storage', handleLogoUpdate);
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(delayedLoad);
      clearTimeout(resizeTimer);
      window.removeEventListener('logo_updated', handleLogoUpdate);
      window.removeEventListener('storage', handleLogoUpdate);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close menu when clicking outside or on overlay (mobile only)
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      const target = event.target as HTMLElement;
      // Close if clicking on overlay or outside the menu
      if (isMobile && isMenuOpen) {
        if (target.classList.contains('mobile-menu-overlay') || 
            (!target.closest('.nav-menu') && !target.closest('.hamburger-menu-toggle') && !target.closest('.mobile-menu-close'))) {
          setIsMenuOpen(false);
        }
      }
    };

    // Prevent body scroll when menu is open
    if (isMobile && isMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMenuOpen, isMobile]);

  // Scroll-based active section highlighting (only on homepage)
  useEffect(() => {
    if (!isHomePage || isAdminPage) {
      setActiveSection('');
      return;
    }

    // Filter sections based on display settings
    const allSections = ['galleries', 'about', 'services', 'instagram', 'blog', 'contact'];
    const sections = allSections.filter(section => {
      switch (section) {
        case 'galleries':
          return displaySettings.showGalleriesSection !== false;
        case 'about':
          return displaySettings.showAboutSection !== false;
        case 'services':
          return displaySettings.showServicesSection !== false;
        case 'instagram':
          return displaySettings.showInstagramSection !== false;
        case 'blog':
          return displaySettings.showBlogSection !== false;
        case 'contact':
          return displaySettings.showContactSection !== false;
        default:
          return true;
      }
    });
    const navHeight = 70; // Navbar height offset

    const updateActiveSection = () => {
      const scrollPosition = window.scrollY + navHeight + 100; // Add some offset for better detection

      // Check URL hash first (if user navigated via hash)
      const hash = window.location.hash.substring(1);
      if (hash && sections.includes(hash)) {
        const hashSection = document.getElementById(hash);
        if (hashSection) {
          const hashTop = hashSection.offsetTop;
          const hashHeight = hashSection.offsetHeight;
          if (scrollPosition >= hashTop && scrollPosition < hashTop + hashHeight) {
            setActiveSection(hash);
            return;
          }
        }
      }

      // Find which section is currently in view
      let currentSection = '';
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionHeight = section.offsetHeight;
          
          // Check if scroll position is within this section
          if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
            currentSection = sections[i];
            break;
          }
        }
      }

      // If we're at the top, highlight first visible section (usually galleries)
      if (scrollPosition < 200 && sections.length > 0) {
        currentSection = sections[0];
      }

      // If no section found but we're past the last section, highlight the last visible section
      if (!currentSection && sections.length > 0 && scrollPosition > document.documentElement.scrollHeight - window.innerHeight - 200) {
        currentSection = sections[sections.length - 1];
      }

      setActiveSection(currentSection);
    };

    // Initial check after a short delay to ensure DOM is ready
    const initialTimeout = setTimeout(() => {
      updateActiveSection();
    }, 100);

    // Throttle scroll events for performance
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateActiveSection();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateActiveSection);
    
    // Also listen for hash changes
    const handleHashChange = () => {
      setTimeout(updateActiveSection, 100);
    };
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      clearTimeout(initialTimeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateActiveSection);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [isHomePage, isAdminPage, displaySettings]);

  // Render navbar on all pages (including admin) for consistent header
  
  return (
    <>
      {/* Mobile Menu Overlay - Outside nav-container for proper z-index stacking */}
      {!isAdminPage && isMobile && isMenuOpen && (
        <div 
          className={`mobile-menu-overlay ${isMenuOpen ? 'active' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsMenuOpen(false);
          }}
          aria-label="Close menu"
        ></div>
      )}
      
      <nav className="navbar" data-track-source="header">
        <div className="nav-container">
          <div className="logo">
            <Link to="/">
              <img 
                src={logo.imageUrl || '/carmen-logo.png'} 
                alt={logo.altText || 'Carmen Lorraine Photography'} 
                className="logo-image"
                loading="eager"
                decoding="async"
                onError={(e) => {
                  const target = e.currentTarget;
                  
                  // Try multiple fallbacks in order
                  const currentSrc = target.src;
                  if (!currentSrc.includes('/carmen-logo.png')) {
                    target.src = '/carmen-logo.png';
                  } else if (!currentSrc.includes('/logo192.png')) {
                    target.src = '/logo192.png';
                  } else if (!currentSrc.includes('/favicon.ico')) {
                    target.src = '/favicon.ico';
                  } else {
                    // Last resort - show a text fallback
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent && !parent.querySelector('.logo-fallback')) {
                      const fallback = document.createElement('span');
                      fallback.className = 'logo-fallback';
                      fallback.textContent = 'CL';
                      fallback.style.cssText = `
                        display: inline-block !important;
                        visibility: visible !important;
                        opacity: 1 !important;
                        font-weight: bold;
                        font-size: 18px;
                        color: var(--color-primary, #8B4513);
                        padding: 8px 12px;
                        border: 2px solid var(--color-primary, #8B4513);
                        border-radius: 4px;
                        background: white;
                      `;
                      parent.appendChild(fallback);
                    }
                  }
                }}
                onLoad={() => {
                  // Remove any fallback text if logo loads successfully
                  const fallback = document.querySelector('.logo-fallback');
                  if (fallback) {
                    fallback.remove();
                  }
                }}
              />
            </Link>
          </div>
          
          {/* Hamburger Menu Button - Only show on mobile and hide on admin pages */}
          {!isAdminPage && isMobile && (
            <button 
              className={`hamburger-menu-toggle ${isMenuOpen ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsMenuOpen(!isMenuOpen);
              }}
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          )}
          
          {/* Navigation Menu - Always visible on desktop, dropdown on mobile */}
          {!isAdminPage && (
            <div className={`nav-menu dropdown-menu ${isMobile ? (isMenuOpen ? 'open' : '') : 'open'}`}>
            {/* Mobile Menu Header */}
            {isMobile && (
              <div className="mobile-menu-header">
                <h3>Menu</h3>
                <button 
                  className="mobile-menu-close"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsMenuOpen(false);
                  }}
                  aria-label="Close menu"
                  type="button"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}
            {!isHomePage && (
              <Link 
                to="/" 
                className="nav-link admin-style-link" 
                onClick={() => {
                  if (isMobile) setIsMenuOpen(false);
                }}
              >
                <i className="fas fa-home"></i>
                <span className="nav-label">Home</span>
              </Link>
            )}
            {(displaySettings.showGalleriesSection !== false) && (
              <div
                className={`nav-dropdown ${galleriesMenuOpen ? 'open' : ''} ${galleriesMenuDismissed ? 'dismissed' : ''}`}
                onMouseEnter={(event) => {
                  if (isMobile) return;
                  if (galleriesMenuLock.current) {
                    const from = event.relatedTarget;
                    const stillInside = from instanceof Node && event.currentTarget.contains(from);
                    if (stillInside || from == null) return;
                    galleriesMenuLock.current = false;
                  }
                  setGalleriesMenuDismissed(false);
                  setGalleriesMenuOpen(true);
                }}
                onMouseLeave={() => {
                  if (isMobile) return;
                  galleriesMenuLock.current = false;
                  setGalleriesMenuOpen(false);
                  setGalleriesMenuDismissed(false);
                }}
              >
                <a
                  href="/galleries"
                  className={`nav-link admin-style-link ${activeSection === 'galleries' || isGalleriesRoute ? 'active' : ''}`}
                  aria-haspopup="true"
                  aria-expanded={galleriesMenuOpen}
                >
                  <i className="fas fa-images"></i>
                  <span className="nav-label">Galleries</span>
                  <span
                    className="nav-dropdown-toggle"
                    role="button"
                    tabIndex={0}
                    aria-label="Open gallery categories"
                    aria-expanded={galleriesMenuOpen}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setGalleriesMenuOpen((open) => !open);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      event.stopPropagation();
                      setGalleriesMenuOpen((open) => !open);
                    }}
                  >
                    <i className={`fas fa-chevron-down nav-dropdown-caret ${galleriesMenuOpen ? 'open' : ''}`} aria-hidden="true"></i>
                  </span>
                </a>
                <div className="nav-dropdown-menu" role="menu">
                  <a
                    href="/galleries"
                    role="menuitem"
                    className={`nav-dropdown-item ${isGalleriesRoute && !activeGalleryCategory ? 'active' : ''}`}
                    onClick={(event) => dismissGalleriesMenu(event)}
                  >
                    All Galleries
                  </a>
                  {portfolioCategories.map((category) => {
                    const slug = (category.slug || '').toLowerCase().trim();
                    if (!slug) return null;
                    const isActive = isGalleriesRoute && activeGalleryCategory === slug;
                    return (
                      <a
                        key={category.id || slug}
                        href={`/galleries/${slug}`}
                        role="menuitem"
                        className={`nav-dropdown-item ${isActive ? 'active' : ''}`}
                        onClick={(event) => dismissGalleriesMenu(event)}
                      >
                        {category.name}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
            <a
              href="/blog"
              className={`nav-link admin-style-link ${location.pathname === '/blog' || location.pathname.startsWith('/blog/') ? 'active' : ''}`}
              onClick={() => {
                if (isMobile) setIsMenuOpen(false);
                setGalleriesMenuOpen(false);
              }}
            >
              <i className="fas fa-blog"></i>
              <span className="nav-label">Blog</span>
            </a>
            <a
              href="/about"
              className={`nav-link admin-style-link ${location.pathname === '/about' ? 'active' : ''}`}
              onClick={() => {
                if (isMobile) setIsMenuOpen(false);
                setGalleriesMenuOpen(false);
              }}
            >
              <i className="fas fa-user" aria-hidden="true"></i>
              <span className="nav-label">About</span>
            </a>
            {(displaySettings.showServicesSection !== false) && (
              <a 
                href="/services"
                className={`nav-link admin-style-link ${location.pathname === '/services' ? 'active' : ''}`}
                onClick={() => {
                  if (isMobile) setIsMenuOpen(false);
                }}
              >
                <i className="fas fa-camera"></i>
                <span className="nav-label">Services</span>
              </a>
            )}
            {(displaySettings.showInstagramSection !== false) && (
              <Link
                to="/#instagram"
                className={`nav-link admin-style-link ${isHomePage && activeSection === 'instagram' ? 'active' : ''}`}
                onClick={openHomepageInstagram}
              >
                <i className="fab fa-instagram"></i>
                <span className="nav-label">Instagram</span>
              </Link>
            )}
            {(displaySettings.showContactSection !== false) && (
              <a 
                href="/contact"
                className={`nav-link admin-style-link ${location.pathname === '/contact' ? 'active' : ''}`}
                onClick={() => {
                  if (isMobile) setIsMenuOpen(false);
                }}
              >
                <i className="fas fa-envelope"></i>
                <span className="nav-label">Contact</span>
              </a>
            )}
          </div>
        )}
        </div>
      </nav>
    </>
  );
};

export default Navbar;
